'use client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { BaseFillIntelligentDuration } from '@/app/components/icons/BaseFillIntelligentDuration';
import { BaseLineCheck2 } from '@/app/components/icons/BaseLineCheck2';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { CURRENCY, SITE } from '@/app/constants';
import { useSyncHeight } from '@/app/hooks';
import type { SubscriptionProductType, TeamTier } from '@/app/types';
import { cn, formatAmount, formatPrice, formatPriceDiscount } from '@/app/utils';
import { useLocale } from '@/i18n/routing';
import {
  CONSULT_BENEFITS,
  CONSULT_PLACEHOLDER,
  ENTERPRISE_ACTIVITY_LINK,
  TEAM_TIER_BENEFITS,
  TEAM_TIER_COMMON_RIGHTS,
  TEAM_TIER_HIGHLIGHTED_RIGHTS,
  TEAM_TITLE_BG,
  type TeamBenefitKey,
} from '../constants';

type TeamPlanCardProps =
  | {
      variant?: 'product';
      tier: TeamTier;
      product: SubscriptionProductType;
      /** 当前 period 是否处于"促销"档位（来自 tabList.isDiscount），控制是否展示折扣徽章 */
      isDiscount?: boolean;
      renderSubscriptionButton?: (product: SubscriptionProductType) => React.ReactNode;
    }
  | {
      variant: 'consult';
      tier?: never;
      product?: never;
      isDiscount?: never;
      renderSubscriptionButton?: () => React.ReactNode;
    };

const monthsInPeriod = (billingPeriod: string | undefined): number => {
  if (billingPeriod === 'yearly') return 12;
  if (billingPeriod === 'quarterly') return 3;
  return 1;
};

export const TeamPlanCard = (props: TeamPlanCardProps) => {
  const t = useTranslations();
  const locale = useLocale();
  useSyncHeight({ selector: '.plan-title-section' });
  useSyncHeight({ selector: '.price-section' });

  const periodLabel = t('payments.plans.per_monthly');

  const cardMeta =
    props.variant === 'consult'
      ? {
          isConsult: true,
          product: undefined,
          tierBenefits: undefined,
          titleText: t('payments.plans.higherTierTitle'),
        }
      : {
          isConsult: false,
          product: props.product,
          tierBenefits: TEAM_TIER_BENEFITS[props.tier],
          titleText: t(
            `payments.plans.tier${props.tier.charAt(0).toUpperCase() + props.tier.slice(1)}` as 'payments.plans.tierBasic',
          ),
        };

  const { isConsult, product, tierBenefits, titleText } = cardMeta;

  // 价格按月折算（与个人版一致），便于跨周期视觉对齐
  const months = monthsInPeriod(product?.billing_period);
  const displayPrice = isConsult ? CONSULT_PLACEHOLDER.price : product ? formatAmount(product.price / months) : '0';
  const subscriptionButton =
    props.variant === 'consult' ? props.renderSubscriptionButton?.() : props.renderSubscriptionButton?.(props.product);

  const renderRightText = (right: TeamBenefitKey): ReactNode => {
    if (!tierBenefits) return t(`payments.plans.${right}`);
    const { seats, subjects, credits, concurrent, submission } = tierBenefits;

    switch (right) {
      case 'team_benefit_seats':
        return t('payments.plans.team_benefit_seats', { seats: seats.toLocaleString() });
      case 'team_benefit_subjects':
        return t('payments.plans.team_benefit_subjects', { subjects: subjects.toLocaleString() });
      case 'team_benefit_credits': {
        const periodCredits = product?.billing_period === 'quarterly' ? credits / 4 : credits;
        return t('payments.plans.team_benefit_credits', { credits: periodCredits.toLocaleString() });
      }
      case 'team_benefit_concurrent':
        return t('payments.plans.team_benefit_concurrent', { concurrent: concurrent.toLocaleString() });
      case 'team_benefit_submission':
        return t('payments.plans.team_benefit_submission', { submission: submission.toLocaleString() });
      case 'team_benefit_events':
        return (
          <>
            {t('payments.plans.team_benefit_events')}{' '}
            <a
              href={ENTERPRISE_ACTIVITY_LINK[SITE]}
              target="_blank"
              rel="noreferrer"
              className="ml-2 text-[#15B8E5] hover:underline"
            >
              {t('payments.plans.viewActivity')}
            </a>
          </>
        );
      case 'team_benefit_training':
        return (
          <>
            {t('payments.plans.team_benefit_training')}
            <CustomTooltip
              content={t('payments.plans.team_training_tips')}
              trigger="auto"
              disableHoverableContent={true}
              container={document.body}
            >
              <BaseLineExclamation className="ml-1 inline shrink-0 align-text-bottom text-sm text-system-white/50" />
            </CustomTooltip>
          </>
        );
      case 'team_benefit_account_manager':
        return (
          <>
            {t('payments.plans.team_benefit_account_manager')}
            <CustomTooltip
              content={t('payments.plans.team_account_manager_tips')}
              trigger="auto"
              disableHoverableContent={true}
              container={document.body}
            >
              <BaseLineExclamation className="ml-1 inline shrink-0 align-text-bottom text-sm text-system-white/50" />
            </CustomTooltip>
          </>
        );
      default:
        return t(`payments.plans.${right}`);
    }
  };

  return (
    <div className="relative flex h-full flex-col items-center justify-start overflow-hidden">
      <div
        className={cn(
          'relative flex h-full w-full flex-col justify-start overflow-hidden rounded-16 border-2 border-system-white16 bg-[#0E1115] px-4 py-6 text-system-white shadow-[inset_0_0_100px_0_rgba(207,231,255,0.08)]',
        )}
      >
        {/* 套餐名称 */}
        <div className="plan-title-section border-system-white12 border-b pb-4 font-semibold text-2xl">
          <div className={cn('flex gap-2', locale === 'zh' ? 'flex-wrap items-center' : 'flex-col items-start')}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="bg-clip-text text-system-white text-transparent" style={TEAM_TITLE_BG}>
                {titleText}
              </span>
              {/* 折扣徽章：仅当父层 tab 处于折扣档位且 SKU 实际有折扣才显示 */}
              {!isConsult && props.isDiscount && product && product.discount > 0 && (
                <span className="rounded-8 bg-[linear-gradient(90deg,#FF7678_0%,#FF383B_100%)] px-2 py-1 font-bold text-white text-xs uppercase italic leading-[1.4]">
                  {SITE === 'china' ? `${formatPriceDiscount(product.discount, 'zh')}折` : `${product.discount}% off`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 价格 */}
        <div className="price-section py-6 text-sm text-system-white">
          <div className="flex items-end gap-1 leading-none">
            <span className="font-semibold text-2xl text-system-white40 leading-[1.4]">{CURRENCY}</span>
            <span className="font-semibold text-[48px] leading-none">{displayPrice}</span>
            {isConsult ? (
              <span className="text-base text-system-white40 leading-[1.4]">{t('payments.plans.teamLimit')}</span>
            ) : (
              <>
                <span className="text-base text-system-white16 leading-[1.4]">/</span>
                <span className="text-base text-system-white40 leading-[1.4]">{periodLabel}</span>
                {product?.original_price && product.discount > 0 ? (
                  <span className="text-base text-system-white40 leading-[1.4] line-through">
                    {formatPrice(product.original_price / months)}
                  </span>
                ) : null}
              </>
            )}
          </div>
          {/* 按周期实付总价（月折算价之外补一行真实付款金额，避免 "/月" 误导）*/}
          {!isConsult && product && (
            <div className="mt-2 text-sm text-system-white leading-5">
              {product.billing_period === 'yearly'
                ? t('payments.plans.billYearlyAs')
                : t('payments.plans.billQuarterlyAs')}
              {formatPrice(product.price)}
            </div>
          )}
        </div>

        {/* 订阅按钮 */}
        {subscriptionButton && <div className="mb-5">{subscriptionButton}</div>}

        {/* 高级权益（plus / pro 相对 basic 提升的项） */}
        {!isConsult && (
          <>
            <div className="mb-3 flex flex-col gap-3 text-left text-sm text-system-white">
              {TEAM_TIER_HIGHLIGHTED_RIGHTS.map((right) => (
                <div key={right} className="flex items-center gap-2">
                  <BaseFillIntelligentDuration className="size-5 shrink-0 text-[#15B8E5]" />
                  <span className="flex flex-1 items-center font-normal">{renderRightText(right)}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 mb-5 h-px bg-system-white08" />
          </>
        )}

        {/* 普通权益 */}
        <div className="flex flex-col gap-3 text-left text-sm text-system-white">
          {(isConsult ? CONSULT_BENEFITS : TEAM_TIER_COMMON_RIGHTS).map((right) => (
            <div key={right} className="flex items-center gap-2">
              <BaseLineCheck2 className="size-4 shrink-0 text-[#888888]" />
              <span className="font-normal">{renderRightText(right)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
